import {
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { useRef } from "react";
import { setErrorMess } from "@/lib/features/statusSlice";
import { Montserrat } from "next/font/google";
import { AnimatePresence, motion } from "framer-motion";
import PowerBiService from "@/services/main/powerBiService";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PowerBi } from "@/models/powerbi";
import useShowCopy from "@/hooks/useShowCopy";
import { useLocale, useTranslations } from "next-intl";
interface BookmarkDashboardMobileProps {
  isLogin?: boolean;
  reportType?: number;
  data?: any[];
  isLoading?: boolean;
  reportId?: string | number;
  handleChangeBookmark?: (state: string) => void;
}
const montserrat = Montserrat({
  subsets: ["latin"],
});

function BookmarkDashboardMobile({
  isLogin = false,
  data,
  isLoading,
  reportId,
  handleChangeBookmark,
}: BookmarkDashboardMobileProps) {
  const t = useTranslations("messages");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { allowShareReport } = useAppSelector((state) => state.report);
  const { isShow, showCopy } = useShowCopy();
   const scrollContainerRef = useRef<HTMLDivElement>(null);
  const handleCopy = (link: string, id: number | string): void => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        showCopy({ id: id });
      })
      .catch(() => {
        dispatch(setErrorMess(t("common_copy_failed")));
      });
  };
  const mutationDelete = useMutation({
    mutationFn: async ({ id }: { id: string | number | null | undefined }) => {
      const result = await PowerBiService.deleteDashboard(id);
      return id;
    },
    onSuccess: (data: any) => {
      queryClient.setQueryData(
        ["bookmark-dashboard", reportId],
        (oldData: any) => {
          return oldData?.filter((bookmark: PowerBi) => bookmark.id !== data);
        }
      );
    },
    onError: (error) => {
      console.error("Delete operation failed:", error);
    },
  });
  const handleDelete = (id: number) => {
    mutationDelete.mutate({ id });
  };

  return (
    <Box
     ref={scrollContainerRef}
      className={`flex flex-col place-items-center justify-center w-full gap-4 items-start h-auto`}
    >
      {isLoading ? (
        <Box className={"flex w-full h-full items-center justify-center"}>
          <CircularProgress />
        </Box>
      ) : (
        data?.map((bookmark: PowerBi) => (
          <Box
            key={`B-${bookmark.id}`}
            className={`w-full cursor-pointer rounded-none !bg-inherit relative group`}
            sx={{
              borderRadius: "0px",
            }}
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex w-full h-full border border-solid rounded-[4px] sm:px-2 sm:py-2 px-2 py-3 bg-white relative"
              onClick={(e: any) => {
                e.stopPropagation();
              }}
            >
              <motion.div
                onClick={(e: any) => {
                  e.stopPropagation();
                  handleChangeBookmark?.(bookmark?.bookmarkState);
                }}
                whileTap={{ scale: 0.95 }}
                className="w-full flex flex-1 items-center justify-start text-gray-400 max-h-12 two-line-ellipsis"
              >
                {bookmark?.bookmarkName}
              </motion.div>
              <Box className="flex items-center justify-end gap-2">
                <AnimatePresence>
                  {isShow[bookmark?.id] && (
                    <motion.div
                      style={{
                        minWidth: "max-content",
                        zIndex: 1000,
                      }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: "easeOut",
                      }}
                      className={`absolute rounded top-[-23px] !-right-3 translate-x-[-50%] px-[8px] py-[4px] flex justify-center items-center !bg-black`}
                    >
                      <Typography
                        className={"!text-[13px] !text-white !font-semibold"}
                        translation-key="common_copy"
                      >
                        {t("common_copy")}
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
                <Box
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleCopy(
                      `${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/interactive/${bookmark?.token}`,
                      bookmark?.id
                    );
                  }}
                  className="rounded-[4px] bg-cimigo-gray sm:p-1 p-1 flex justify-center items-center flex-col gap-[8px]"
                >
                  <ContentCopyIcon
                    sx={{
                      color: "black",
                      width: { xs: "24px", sm: "24px" },
                      height: { xs: "24px", sm: "24px" },
                      cursor: "pointer",
                      p: "0 4px",
                    }}
                  />
                </Box>
                <Box
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleDelete(bookmark?.id);
                  }}
                  className="rounded-[4px] bg-cimigo-gray sm:p-1 p-1 flex justify-center items-center flex-col gap-[8px]"
                >
                  <DeleteOutlineIcon
                    sx={{
                      color: "black",
                      width: { xs: "24px", sm: "24px" },
                      height: { xs: "24px", sm: "24px" },
                      cursor: "pointer",
                      p: "0 4px",
                    }}
                  />
                </Box>
              </Box>
            </motion.div>
          </Box>
        ))
      )}
      {data?.length === 0 && (
        <Typography translation-key="bookmark_review_no_bookmarks" className="!text-cimigo-black !italic p-4 text-center col-span-2">
          {t("bookmark_review_no_bookmarks")}
        </Typography>
      )}
    </Box>
  );
}

export default BookmarkDashboardMobile;
